/**
 * Toast — `role="status"` theo docs/08 §8.7, dùng lại class `.toasts`/`.toast`
 * của design system.
 *
 * FR-006: ba mức (thành công / cảnh báo / lỗi), tự ẩn sau 4 giây, có nút đóng.
 * Prototype `assets/app.js` chỉ dựng một mức và không có nút đóng — bố cục lấy
 * theo prototype, còn hành vi lấy theo FR.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { useTLa } from '../lib/i18n';

type ToastTone = 'ok' | 'warn' | 'bad';

/** Nút hành động trong toast — FR-040 dùng cho "Hoàn tác" trong 10 giây. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
}

export interface ToastOptions {
  /** Thời gian tự ẩn, ms. Mặc định 4000 (FR-006). */
  durationMs?: number;
  action?: ToastAction;
}

interface ToastValue {
  toast: (message: string, options?: ToastOptions) => void;
  toastWarn: (message: string, options?: ToastOptions) => void;
  toastError: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastValue | null>(null);
let nextId = 1;

/** Tự ẩn sau 4 giây (FR-006). */
const AUTO_HIDE_MS = 4000;

/**
 * Bộ icon không có biểu tượng riêng cho lỗi, nên cảnh báo và lỗi dùng chung
 * `warn` và phân biệt bằng màu (vàng / đỏ) — cách nhận biết vẫn rõ ràng.
 */
const TONE_ICON: Record<ToastTone, 'check' | 'warn'> = {
  ok: 'check',
  warn: 'warn',
  bad: 'warn',
};

const TONE_CLASS: Record<ToastTone, string> = {
  ok: 'tick',
  warn: 'tick is-warn',
  bad: 'tick is-bad',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useTLa();
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone, options?: ToastOptions) => {
      const id = nextId++;
      const item: ToastItem = { id, message, tone };
      if (options?.action) item.action = options.action;
      setItems((prev) => [...prev, item]);
      setTimeout(() => dismiss(id), options?.durationMs ?? AUTO_HIDE_MS);
    },
    [dismiss],
  );

  const value = useMemo<ToastValue>(
    () => ({
      toast: (message, options) => push(message, 'ok', options),
      toastWarn: (message, options) => push(message, 'warn', options),
      toastError: (message, options) => push(message, 'bad', options),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className="toast">
            <span className={TONE_CLASS[item.tone]}>
              <Icon name={TONE_ICON[item.tone]} size={16} />
            </span>
            <span>{item.message}</span>
            {item.action ? (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  item.action?.onClick();
                  dismiss(item.id);
                }}
              >
                {item.action.label}
              </button>
            ) : null}
            <button
              type="button"
              className="toast-close"
              onClick={() => dismiss(item.id)}
              aria-label={t('common.closeToast')}
            >
              <Icon name="close" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast phải nằm trong <ToastProvider>');
  return ctx;
}
